import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

import { operatorMenuConfig, isMenuItemVisible } from '@components/operator/menuConfig';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { usePlan } from '@contexts/operator/PlanContext';
import { useStorageMode } from '@contexts/operator/StorageModeContext';

import operatorMenuService from '@services/operator/menuService';

// 根據權限、方案功能與單貨主模式過濾選單項目
// 使用 menuConfig.jsx 匯出的 isMenuItemVisible() 作為單一可信來源，
// 確保選單與路由閘門 (operatorRouter.jsx) 使用相同判斷邏輯。
// @param items [Array] 選單項目陣列
// @param planContext [Object] { permissions, enabledFeatures, isStandard }
// @return [Array] 過濾後的選單項目
const filterMenuItemsByVisibility = (items, planContext) => {
  return items
    .filter(item => isMenuItemVisible(item, planContext))
    .map(item => {
      // 處理子選單
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuItemsByVisibility(item.children, planContext);
        // 如果所有子項目都被過濾掉，則隱藏父項目
        if (filteredChildren.length === 0) {
          return null;
        }
        return { ...item, children: filteredChildren };
      }
      return item;
    })
    .filter(item => item !== null);
};

const OperatorMenuContext = createContext();

export const OperatorMenuProvider = ({ children }) => {
  const { permissions, isAuthenticated, loading: authLoading } = useOperatorAuth();
  const { enabledFeatures, isStandard, isEnterprise } = usePlan();
  const { mode: storageMode } = useStorageMode();
  const [menuSettings, setMenuSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 將 plan + permissions + storageMode 整合為單一 context，供 isMenuItemVisible 使用
  const planContext = useMemo(
    () => ({ permissions, enabledFeatures, isStandard, isEnterprise, storageMode }),
    [permissions, enabledFeatures, isStandard, isEnterprise, storageMode]
  );

  // 載入選單設定
  const loadMenuSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await operatorMenuService.getMenuSettings();
      setMenuSettings(settings);
    } catch (err) {
      console.error('Failed to load menu settings:', err);
      setError(err.message);
      setMenuSettings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 只有在已認證時才載入選單設定（等待 AuthContext 初始化完成）
  useEffect(() => {
    // 等待認證狀態確認完成
    if (authLoading) {
      return;
    }

    // 只有在已認證時才載入選單設定
    if (isAuthenticated) {
      loadMenuSettings();
    } else {
      // 未認證時清空選單設定
      setMenuSettings([]);
      setLoading(false);
      setError(null);
    }
  }, [isAuthenticated, authLoading, loadMenuSettings]);

  // 取得 pinned 選單項目（扁平化列表，排除父選單容器）
  const getPinnedMenuItems = useCallback(() => {
    if (!menuSettings || menuSettings.length === 0) {
      return [];
    }

    // 找出所有被 pin 的選單項目
    const pinnedKeys = menuSettings
      .filter(setting => setting.pinned)
      .map(setting => setting.menu_key);

    // 建立扁平化的選單項目 map
    const flatMenuMap = new Map();

    const flattenMenu = (items, parentKey = null) => {
      items.forEach(item => {
        flatMenuMap.set(item.key, { ...item, parentKey });
        if (item.children) {
          flattenMenu(item.children, item.key);
        }
      });
    };

    flattenMenu(operatorMenuConfig);

    // 返回被 pin 的項目（排除真正的父選單容器，保留獨立選單和子選單）
    return pinnedKeys
      .map(key => flatMenuMap.get(key))
      .filter(item => item !== undefined)
      .filter(item => !item.isParent);
  }, [menuSettings]);

  // 取得已排序且過濾可見的選單配置
  // 過濾邏輯：
  // 1. 先根據 isMenuItemVisible 過濾（權限 + requiredFeature + hideInStandard）
  // 2. 再套用用戶的 visible 設定（隱藏用戶不想看到的項目）
  // 3. 最後排序
  const getOrderedMenuConfig = useCallback(() => {
    // 步驟 1：根據權限、方案功能、單貨主模式過濾選單
    const visibilityFilteredConfig = filterMenuItemsByVisibility(operatorMenuConfig, planContext);

    // 如果沒有自定義設定，直接返回過濾後的配置
    if (!menuSettings || menuSettings.length === 0) {
      return visibilityFilteredConfig;
    }

    // 步驟 2：套用用戶的 visible 設定和排序
    const settingsMap = new Map(
      menuSettings.map(setting => [setting.menu_key, setting])
    );

    // 過濾和排序選單項目
    const processMenuItems = (items) => {
      return items
        .map(item => {
          const setting = settingsMap.get(item.key);

          // 如果有自定義設定且被隱藏，則過濾掉
          if (setting && !setting.visible) {
            return null;
          }

          // 處理子選單
          const processedItem = { ...item };
          if (item.children) {
            processedItem.children = processMenuItems(item.children);
            // 如果所有子項目都被隱藏，則隱藏父項目
            if (processedItem.children.length === 0) {
              return null;
            }
          }

          return {
            ...processedItem,
            position: setting?.position ?? 999,
            pinned: setting?.pinned ?? false
          };
        })
        .filter(item => item !== null)
        .sort((a, b) => a.position - b.position);
    };

    return processMenuItems(visibilityFilteredConfig);
  }, [menuSettings, planContext]);

  // 批量更新選單設定
  const updateMenuSettings = useCallback(async (items) => {
    try {
      setError(null);
      await operatorMenuService.batchUpdate(items);
      await loadMenuSettings();
      return { success: true };
    } catch (err) {
      console.error('Failed to update menu settings:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [loadMenuSettings]);

  // 切換 pin 狀態
  const togglePin = useCallback(async (menuKey) => {
    try {
      setError(null);
      const updatedMenu = await operatorMenuService.togglePin(menuKey);
      await loadMenuSettings();
      return { success: true, data: updatedMenu };
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [loadMenuSettings]);

  const value = useMemo(() => ({
    menuSettings,
    loading,
    error,
    getPinnedMenuItems,
    getOrderedMenuConfig,
    updateMenuSettings,
    togglePin,
    refreshMenuSettings: loadMenuSettings
  }), [menuSettings, loading, error, getPinnedMenuItems, getOrderedMenuConfig, updateMenuSettings, togglePin, loadMenuSettings]);

  return (
    <OperatorMenuContext.Provider value={value}>
      {children}
    </OperatorMenuContext.Provider>
  );
};

export const useOperatorMenu = () => {
  const context = useContext(OperatorMenuContext);
  if (!context) {
    throw new Error('useOperatorMenu must be used within an OperatorMenuProvider');
  }
  return context;
};

export default OperatorMenuContext;
